import { useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { isOpenIddict } from "./authConfig";

/**
 * The authentication surface the rest of the portal uses.
 *
 * Deliberately small: everything the app actually needs and nothing provider specific, so the
 * Auth0 implementation can be deleted without touching callers.
 */
export interface BeamXRAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: Error;
  /** Sends the user to the provider to sign in. */
  signIn: () => void;
  /** Signs the user out of both the app and the provider. */
  signOut: () => void;
  /** A valid access token, renewing it first if the cached one has expired. */
  getAccessToken: () => Promise<string | undefined>;
  /**
   * Forces a brand new access token even if the current one is still valid. Needed after
   * something changes the claims baked into the token, such as creating a company.
   */
  renewAccessToken: () => Promise<string | undefined>;
}

/**
 * Provider agnostic authentication hook.
 *
 * Both underlying hooks are called unconditionally because hooks cannot be called
 * conditionally. Only the one matching the configured provider has a mounted context, so the
 * other returns its default state and is ignored.
 *
 * The functions returned keep the same identity across renders. The API client and every
 * service callback list getAccessToken as a dependency, so a new function each render made
 * every data-loading effect re-run on every render, and an effect that sets state then looped
 * forever. They read the provider through refs so they always see its current state.
 */
export function useBeamXRAuth(): BeamXRAuth {
  const auth0 = useAuth0();
  const oidc = useOidcAuth();

  const auth0Ref = useRef(auth0);
  auth0Ref.current = auth0;
  const oidcRef = useRef(oidc);
  oidcRef.current = oidc;

  const signIn = useCallback(() => {
    if (isOpenIddict) {
      void oidcRef.current.signinRedirect();
    } else {
      void auth0Ref.current.loginWithRedirect();
    }
  }, []);

  const signOut = useCallback(() => {
    if (isOpenIddict) {
      // Ends the session at the identity server as well as locally, so the next sign in is
      // not silently completed from a surviving cookie.
      void oidcRef.current.signoutRedirect();
    } else {
      void auth0Ref.current.logout({
        logoutParams: { returnTo: window.location.origin },
      });
    }
  }, []);

  const getAccessToken = useCallback(async () => {
    if (!isOpenIddict) {
      return auth0Ref.current.getAccessTokenSilently();
    }

    // oidc-client-ts renews in the background, but a token can still be expired if the tab
    // was suspended, so renew on demand rather than handing back a stale one.
    const user = oidcRef.current.user;
    if (user && !user.expired) {
      return user.access_token;
    }

    const renewed = await oidcRef.current.signinSilent();
    return renewed?.access_token;
  }, []);

  const renewAccessToken = useCallback(async () => {
    if (!isOpenIddict) {
      return auth0Ref.current.getAccessTokenSilently({ cacheMode: "off" });
    }

    const renewed = await oidcRef.current.signinSilent();
    return renewed?.access_token;
  }, []);

  return {
    isLoading: isOpenIddict ? oidc.isLoading : auth0.isLoading,
    isAuthenticated: isOpenIddict ? oidc.isAuthenticated : auth0.isAuthenticated,
    error: isOpenIddict ? oidc.error : auth0.error,
    signIn,
    signOut,
    getAccessToken,
    renewAccessToken,
  };
}
